import { Component, ErrorInfo, ReactNode, Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import ErrorUI from './ErrorUI';
import CatastrophicErrorUI from './CatastrophicErrorUI';
import { AppSkeleton } from './AppSkeleton';
import { activateDemoMode } from '../services/demoAssets';
import { resizeImageToTarget, convertToGrayscale } from '../services/orchestrationService';
import { logError, detectJavaScriptError } from '../services/errorDetectionService';
import type { ErrorContext } from '../types/errors';
import './ErrorBoundary.css';

/**
 * Story 7.3 Task 2: Lazy load GameComponent for chunk splitting
 * Must be consistent with App.tsx lazy import
 */
const GameComponent = lazy(() => import('./GameComponent'));

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorContext: ErrorContext | null;
  playingDemo: boolean;
  demoAssets: {
    character?: string;
    obstacle?: string;
    background?: string;
  } | null;
  isCatastrophic: boolean; // Story 6.3 Task 5: Track catastrophic errors
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private unhandledRejectionHandler: (event: PromiseRejectionEvent) => void;
  private errorHandler: OnErrorEventHandler;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorContext: null,
      playingDemo: false,
      demoAssets: null,
      isCatastrophic: false, // Story 6.3 Task 5
    };

    // Async error handler with error detection logging
    this.unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      const isCatastrophic = error.name === 'CatastrophicError'; // Story 6.3 Task 5

      const errorContext = detectJavaScriptError(error);

      logError({
        ...errorContext,
        message: `Unhandled promise rejection: ${errorContext.message}`,
      });
      this.setState({ hasError: true, error, errorContext, isCatastrophic });
    };

    // Global error handler for uncaught errors
    this.errorHandler = (message, source, lineno, colno, error) => {
      const actualError = error || (typeof message === 'string' ? new Error(message) : new Error('Unknown error'));
      const isCatastrophic = actualError.name === 'CatastrophicError'; // Story 6.3 Task 5

      const errorContext = detectJavaScriptError(actualError);

      logError(errorContext);
      this.setState({ hasError: true, error: actualError, errorContext, isCatastrophic });

      // Return true to prevent default browser error handling
      return true;
    };

    window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);
    window.onerror = this.errorHandler;
  }

  componentWillUnmount(): void {
    // Clean up event listeners to prevent memory leaks
    window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
    window.onerror = null;
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isCatastrophic = error.name === 'CatastrophicError'; // Story 6.3 Task 5
    const errorContext = detectJavaScriptError(error);

    // Story 8.1 Task 3.3: Add component stack to Sentry context
    if (errorInfo?.componentStack) {
      Sentry.setContext('react', {
        componentStack: errorInfo.componentStack,
      });
    }

    logError(errorContext);
    this.setState({ error, errorInfo, errorContext, isCatastrophic });
  }


  handlePlayDemo = async (): Promise<void> => {
    try {
      // Story 6.2 Code Review Fix: Use activateDemoMode() for full demo activation
      // This includes performance logging, pre-flight checks, and metadata
      const demoAssets = await activateDemoMode();

      // CRITICAL: Convert to grayscale and resize demo assets before loading into game
      // 1. Convert to true grayscale (demo assets may have color tints)
      // 2. Resize from 30x30, 52x480 to 128x128, 256x512 to pass assetLoader validation
      console.log('[ErrorBoundary] Converting demo assets to grayscale and resizing...');

      // Step 1: Convert to grayscale
      const [grayCharacter, grayObstacle, grayBackground] = await Promise.all([
        convertToGrayscale(demoAssets.character),
        convertToGrayscale(demoAssets.obstacle),
        convertToGrayscale(demoAssets.background),
      ]);

      // Step 2: Resize to target dimensions
      const [resizedCharacter, resizedObstacle, resizedBackground] = await Promise.all([
        resizeImageToTarget(grayCharacter, 'character'),
        resizeImageToTarget(grayObstacle, 'obstacle'),
        resizeImageToTarget(grayBackground, 'background'),
      ]);

      this.setState({
        playingDemo: true,
        demoAssets: {
          character: resizedCharacter,
          obstacle: resizedObstacle,
          background: resizedBackground,
        },
      });
    } catch (error) {
      console.error('Failed to load demo assets:', error);
      // Fallback: reload page if demo assets fail
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Story 6.3 Task 5: Catastrophic error - show text-only fallback with Refresh Page button
      if (this.state.isCatastrophic) {
        return (
          <>
            <CatastrophicErrorUI />

            {/* Development mode: Show error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="error-boundary-container">
                <div className="error-boundary-content">
                  <details className="error-details">
                    <summary>Error Details (Development Only)</summary>
                    <pre>{this.state.error.toString()}</pre>
                    <pre>{this.state.errorInfo?.componentStack}</pre>
                  </details>
                </div>
              </div>
            )}
          </>
        );
      }

      // If playing demo, render GameComponent with demo assets
      // Story 7.3 Task 2: Suspense wrapper for lazy-loaded GameComponent
      if (this.state.playingDemo && this.state.demoAssets) {
        return (
          <Suspense fallback={<AppSkeleton />}>
            <GameComponent
              customAssets={{
                assets: this.state.demoAssets,
                metadata: {},
              }}
              onBackClick={() => window.location.reload()}
            />
          </Suspense>
        );
      }

      // Show error UI with Play Demo button
      return (
        <>
          <ErrorUI
            errorType={this.state.errorContext?.errorType || 'javascript'}
            onPlayDemo={this.handlePlayDemo}
          />

          {/* Development mode: Show error details */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div className="error-boundary-container">
              <div className="error-boundary-content">
                <details className="error-details">
                  <summary>Error Details (Development Only)</summary>
                  <pre>{this.state.error.toString()}</pre>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </details>
              </div>
            </div>
          )}
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
